import { AdminUI, useTheme } from "react-admin";

import { resources as all_resources } from "./resourcesList";
import Dashboard from "./views/Dashboard";
import { cloneElement, useEffect, useState } from "react";
import { asArray, getValue, humanize } from "./ra-lb-tools/utils/tools";
import set from "lodash/set";
import get from "lodash/get";
import MyLayout from "./theme/layout";
import { useMyContext } from "./ra-lb-tools/components/myContext";
import { Loading } from "./components/customLoading";
import { debug_log } from "./ra-lb-tools/utils/debugLog";

const resourceActions = ["list", "edit", "create", "show"];

const addLabelToResource = (_res, resource) => {
  if (!get(_res, "props.options.label")) {
    const label = humanize(resource);
    set(_res, "props.options.label", label);
  }
};

const removeDeniedActions = (_res, allowedActions) => {
  const removedActions = resourceActions.filter(
    (action) => !allowedActions.includes(action)
  );
  const newProps = {
    ..._res.props,
    ...Object.fromEntries(removedActions.map((key) => [key, undefined])),
  };

  //TODO: check custom inner routes
  return cloneElement(_res, newProps);
};

const getDisabledResource = (_res, resource, reason, can = "") => {
  debug_log(
    `sanitizeResource: resource '${resource}' ${reason} (${JSON.stringify(
      can
    )})`
  );

  if (!get(_res, "props.options.label").startsWith("[")) {
    let newProps = { ..._res.props };
    set(
      newProps,
      "options.label",
      `[${reason}]` + get(newProps, "options.label")
    );
    set(newProps, "options.debugOnly", true);
    set(newProps, "options.isRestrictedElement", true);
    return cloneElement(_res, newProps);
  }

  return _res;
};

const sanitizeResource = (_res, acp) => {
  const resource = getValue(_res, "props.name");
  const can = getValue(_res, "props.options.can");
  const skipAllowedActionsCheck = getValue(
    _res,
    "props.options.skipAllowedActionsCheck"
  );
  const allowedActions = acp.allowedActions({ resource });

  addLabelToResource(_res, resource);

  let sanitizedResource = null;

  if (skipAllowedActionsCheck) {
    sanitizedResource = cloneElement(_res);
  } else {
    sanitizedResource = removeDeniedActions(_res, allowedActions);

    if (!allowedActions.length) {
      return getDisabledResource(
        sanitizedResource,
        resource,
        "NoAllowedActions"
      );
    }

    if (!resourceActions.filter((x) => allowedActions.includes(x)).length) {
      return getDisabledResource(sanitizedResource, resource, "NoValidActions");
    }
  }

  if (can === true) {
    return sanitizedResource;
  }

  if (can && !acp.can(can)) {
    return getDisabledResource(sanitizedResource, resource, "Disabled", can);
  }

  return sanitizedResource;
};

const hasAllowedPath = (routeComp, acp) => {
  const path = getValue(routeComp, "props.path");
  return (
    getValue(routeComp, "props.isMyCustomRoute") ||
    !path ||
    acp.isAllowedPath({ path })
  );
};

const validateRouteComponent = (routeComp, acp) => {
  const isAllowedPath = hasAllowedPath(routeComp, acp);

  const can = getValue(routeComp, "props.can");
  const isAllowedItem = !can || acp.can(can);

  if (isAllowedPath && isAllowedItem) {
    return cloneElement(routeComp);
  } else {
    debug_log(`validateRouteComponent: route '' not allowed`);
    return null;
  }
};

const sanitizeCustomRoutes = (_res, acp) => {
  const children = asArray(getValue(_res, "props.children", [])).flat(1);
  const newChildren = children
    .map((_customRoute) => validateRouteComponent(_customRoute, acp))
    .filter((v) => v);

  const res = cloneElement(_res, { children: newChildren });

  return res;
};

const LoadingUI = (props) => {
  return <Loading loadingSecondary="Loading routes" {...props} />;
};

export const AppUI = () => {
  const [resources, setResources] = useState([]);
  const { acp } = useMyContext();

  const [theme, setTheme] = useTheme();

  useEffect(() => {
    setResources(allowed_resources);
    setTheme(theme);
  }, []);

  const allowed_resources = all_resources
    .map((_res) => {
      const type = getValue(_res, "type.raName");
      let res = null;

      if (type == "Resource") {
        res = sanitizeResource(_res, acp);
      } else {
        //custom routes
        res = sanitizeCustomRoutes(_res, acp);
      }

      return res;
    })
    .filter((v) => v);

  return (
    <AdminUI
      disableTelemetry
      ready={LoadingUI}
      layout={MyLayout}
      dashboard={Dashboard}
    >
      {resources}
    </AdminUI>
  );
};
